import type {
  PlaybookId,
  PlaybookView,
} from "@repo/app-types/entities/playbook";
import type { PlaybookViewDefaults } from "@repo/app-types/entities/playbookViewDefaults";
import { Button, ButtonVariant } from "@repo/ui/Buttons/button";
import { Size } from "@repo/ui/helpers/helpers";
import { SimpleModal, SimpleModalVariant } from "@repo/ui/Modal/modal";
import type { FC } from "react";
import { useUpdatePlaybookViewDefaultsMutation } from "../../api/entities/playbook.js";
import { useAppDispatch } from "../../hooks/hook.js";
import {
  NotificationType,
  showNotification,
} from "../notificationsOverlay/notificationsSlice.js";

interface SetPlaybookViewDefaultActionProps {
  onClick: () => void;
}

export const SetPlaybookViewDefaultAction: FC<
  SetPlaybookViewDefaultActionProps
> = (props) => (
  <Button
    className="w-full justify-center"
    size={Size.SMALL}
    variant={ButtonVariant.Secondary}
    onClick={props.onClick}
  >
    Set default for everyone
  </Button>
);

interface SetPlaybookViewDefaultConfirmationProps {
  playbookId: PlaybookId;
  view: PlaybookView;
  /** Only the settings being saved; other defaults remain unchanged. */
  defaults: PlaybookViewDefaults;
  kind?: "filters" | "columns";
  isOpen: boolean;
  onOpenChange: (isOpen: boolean) => void;
}

/**
 * Saves the current filters or columns as the playbook's default for
 * this kind of page.
 */
export const SetPlaybookViewDefaultConfirmation: FC<
  SetPlaybookViewDefaultConfirmationProps
> = (props) => {
  const {
    playbookId,
    view,
    defaults,
    kind = "filters",
    isOpen,
    onOpenChange,
  } = props;
  const dispatch = useAppDispatch();
  const [updateViewDefaults, { isLoading: isSaving }] =
    useUpdatePlaybookViewDefaultsMutation();

  const handleConfirm = async () => {
    try {
      await updateViewDefaults({ id: playbookId, view, defaults }).unwrap();

      onOpenChange(false);
      showNotification(dispatch, {
        id: `playbook-${kind}-default-updated-${playbookId}-${view}`,
        primaryMessage: `Default ${kind} updated for everyone`,
        type: NotificationType.SUCCESS,
      });
    } catch {
      showNotification(dispatch, {
        id: `playbook-${kind}-default-failed-${playbookId}-${view}`,
        primaryMessage: `Couldn't update the default ${kind}`,
        type: NotificationType.FAILURE,
      });
    }
  };

  return (
    <SimpleModal
      isOpen={isOpen}
      variant={SimpleModalVariant.Standard}
      title={`Set these ${kind} as the default for everyone?`}
      subtitle={
        kind === "columns"
          ? `Teammates without saved column preferences will start with these columns on the ${view === "calls" ? "Calls" : "Team"} pages for this playbook. Everyone can still customize their own columns.`
          : view === "calls"
            ? "Teammates will start with these filters on the Calls pages for this playbook. They can still make changes and reset to this default."
            : "Teammates will start with these filters on the performance pages for this playbook. They can still make changes and reset to this default."
      }
      confirmButtonText="Set default"
      isPrimaryButtonLoading={isSaving}
      onCancel={() => onOpenChange(false)}
      onConfirm={() => void handleConfirm()}
    />
  );
};
